import { type Campaign, type HistoryLine, type Programme, type Wallet } from './rewards-api';

/**
 * The words and numbers the Rewards page shows, worked out from the
 * platform's own answers only. The frame's "1 PENNY per £1", "50 PENNY per
 * review", "500 PENNY for a friend" and "100 PENNY is worth £1" are demo
 * copy: every rate here is the programme in force, and a way to earn the
 * platform does not have (reviews) is not drawn at all.
 */

export function money(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
}

export const units = (n: number): string => n.toLocaleString('en-GB');

export const dateOf = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** "Worth £12.40", or nothing when no programme prices a PENNY. */
export function worth(wallet: Pick<Wallet, 'valueMinorPerUnit' | 'currency'>, n: number): string | undefined {
  if (wallet.valueMinorPerUnit === null || wallet.currency === null) return undefined;
  return `Worth ${money(n * wallet.valueMinorPerUnit, wallet.currency)}`;
}

/** "100 PENNY is worth £1.00" when a whole number of PENNY makes a pound; else what one PENNY is worth. */
export function unitValue(programme: Pick<Programme, 'valueMinorPerUnit' | 'currency' | 'unit'>): string {
  const per = 100 / programme.valueMinorPerUnit;
  if (Number.isInteger(per) && per > 1)
    return `${units(per)} ${programme.unit} is worth ${money(100, programme.currency)}`;
  return `1 ${programme.unit} is worth ${money(programme.valueMinorPerUnit, programme.currency)}`;
}

/** A programme whose own name says its terms are not final (beta: "TEST terms — not approved"). */
export const isTestProgramme = (programme: Pick<Programme, 'name'>): boolean =>
  programme.name !== undefined && /\btest\b|not approved/i.test(programme.name);

export interface Way {
  readonly t: string;
  readonly d: string;
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

/** How you earn, from the programme in force and its referral terms. */
export function earnWays(programme: Programme, referral: Wallet['referral']['terms']): readonly Way[] {
  const ways: Way[] = [];
  const unit = programme.unit;
  const pound = money(100, programme.currency).replace(/\.00$/, '');
  const when =
    programme.qualifyOn === 'ORDER_COMPLETED'
      ? 'once the order is delivered or collected'
      : 'once the order is confirmed';
  const held =
    programme.holdDays > 0
      ? `, after ${units(programme.holdDays)} ${plural(programme.holdDays, 'day', 'days')}`
      : '';
  const earnTitle =
    programme.earn.method === 'PER_POUND'
      ? `${units(programme.earn.unitsPerPound)} ${unit} per ${pound}`
      : `${unit} worth ${String(programme.earn.percentBps / 100)}% of what you spend`;
  ways.push({
    t: earnTitle,
    d: `On every eligible order. Pending when you pay, ready to use ${when}${held}.`,
  });

  if (programme.bookingCompletedUnits > 0) {
    ways.push({
      t: `${units(programme.bookingCompletedUnits)} ${unit} per appointment`,
      d: 'When an appointment you booked is completed',
    });
  }

  if (referral !== null && referral.referrerUnits > 0) {
    const min =
      referral.minQualifyingSpendMinor > 0
        ? ` of ${money(referral.minQualifyingSpendMinor, programme.currency)} or more`
        : '';
    const them = referral.referredUnits > 0 ? ` They get ${units(referral.referredUnits)} ${unit} too.` : '';
    ways.push({
      t: `${units(referral.referrerUnits)} ${unit} for a friend`,
      d: `When someone you invite completes their first order${min}.${them}`,
    });
  }
  return ways;
}

/** How PENNY is spent: at checkout, within the programme's own limits. */
export function spendRules(programme: Programme): readonly string[] {
  const r = programme.redemption;
  return [
    `Use ${programme.unit} at checkout, ${units(r.minUnits)} or more at a time, on up to ${String(
      r.maxPercentOfEligibleBps / 100,
    )}% of the eligible items. You always pay at least ${money(r.minPayableMinor, programme.currency)}.`,
    `${programme.unit} expires ${units(programme.expiryDays)} ${plural(programme.expiryDays, 'day', 'days')} after it becomes ready to use.`,
    programme.regulatedMedicines,
  ];
}

export interface HistoryView {
  readonly key: string;
  readonly t: string;
  readonly d: string;
  readonly v: string;
  readonly tone: 'in' | 'out' | 'state';
  readonly orderId?: string;
}

/** One ledger line as the frame's History row: the server's own description, date, signed amount. */
export function historyView(line: HistoryLine): HistoryView {
  const n = units(line.units);
  const reason = line.reason !== undefined && line.reason !== '' ? ` · ${line.reason}` : '';
  return {
    key: line.id,
    t: line.description,
    d: `${dateOf(line.at)}${reason}`,
    v: line.direction === 'IN' ? `+${n}` : line.direction === 'OUT' ? `−${n}` : `${n} confirmed`,
    tone: line.direction === 'IN' ? 'in' : line.direction === 'OUT' ? 'out' : 'state',
    ...(line.orderId !== undefined ? { orderId: line.orderId } : {}),
  };
}

/** A campaign in a line: "2× PENNY", "+200 PENNY". */
export function campaignTitle(campaign: Campaign, unit: string): string {
  if (campaign.kind === 'MULTIPLIER' && campaign.multiplier !== undefined)
    return `${String(campaign.multiplier)}× ${unit}`;
  if (campaign.kind === 'BONUS_UNITS' && campaign.bonusUnits !== undefined)
    return `+${units(campaign.bonusUnits)} ${unit}`;
  return unit;
}

export const HISTORY_PAGE = 10;
